import React, { useEffect, useState } from 'react';
import { aopApi, formatWhen } from '../aopApi';

// Curated narrative catalog (role, capabilities, guardrails, handoffs). The
// operational fields — status, runs, durations, models, skill versions,
// schedules — are overridden with live data from /api/v1/aop/agents for every
// registered agent; placeholder agents keep their honest 'planned' values.
const AGENTS = [
  {
    id: 'orchestrator_bot',
    name: 'ChrisGPT',
    color: '#10b981',
    bg: 'rgba(16,185,129,0.1)',
    border: 'rgba(16,185,129,0.25)',
    role: 'Sales Intelligence',
    phase: 'phase-1',
    phaseLabel: 'Phase 1',
    description: 'Generates daily and weekly opportunity briefs from Salesforce pipeline data, and checks open opportunities every hour. Monitors account health, flags at-risk deals, and surfaces upsell signals.',
    capabilities: ['Salesforce query', 'Pipeline analysis', 'Daily brief generation', 'Weekly summary', 'Hourly opp check', 'Deal risk scoring'],
    tools: ['Salesforce read', 'CSD lookup', 'Audit log', 'Reports', 'Slack draft'],
    outputs: ['Daily executive brief', 'Weekly leadership report', 'Hourly opp check', 'Escalation packets'],
    handoffs: ['Braiks for process review', 'Ainna for data analysis', 'Baitty for CSM follow-up'],
    guardrails: ['Read-only source queries', 'Create pending actions for outbound summaries', 'Cite source records in every report'],
    queue: 'executive-briefs',
    approvalPolicy: 'Pending action for Slack or email send',
    nextAction: 'Connect to mocked active Opportunity queue for smoke run.',
    status: 'active',
    lastRun: '9:02 AM today',
    runs: { total: 47, success: 44, failed: 3 },
    avgDuration: '6m 14s',
    model: 'claude-opus-4-8',
    skillSource: 'skills/executive/SKILL.md',
    promptVersion: 'git commit at run time',
    schedule: '9:00 AM daily',
    triggerType: 'Scheduled',
  },
  {
    id: 'copy_bot',
    name: 'Samaintha',
    color: '#7c3aed',
    bg: 'rgba(124,58,237,0.1)',
    border: 'rgba(124,58,237,0.25)',
    role: 'Copy & Creative',
    phase: 'phase-1',
    phaseLabel: 'Phase 1',
    description: 'Produces complete copy packages for onboarding deals — ad copy, email sequences, landing page text, and brand voice guidelines based on the client brief.',
    capabilities: ['Copy generation', 'Email sequences', 'Brand analysis', 'Landing page copy', 'Subject line variants'],
    tools: ['Salesforce read', 'CSD forms', 'Prompt library', 'Pending CSD action', 'Task comment draft'],
    outputs: ['Copy package', 'Missing input report', 'CSD update draft'],
    handoffs: ['Raimond for design packet', 'Devain for implementation packet', 'Baitty when approval data is incomplete'],
    guardrails: ['Never invent offer or legal copy', 'Use CSD as source of truth', 'External writes require approval'],
    queue: 'copy-packages',
    approvalPolicy: 'Pending action for CSD/task updates',
    nextAction: 'Add fixture response for approved Opportunity with complete CSD requirements.',
    status: 'active',
    lastRun: '8:28 AM today',
    runs: { total: 31, success: 30, failed: 1 },
    avgDuration: '9m 45s',
    model: 'claude-opus-4-8',
    skillSource: 'skills/campaign-copy/SKILL.md',
    promptVersion: 'git commit at run time',
    schedule: 'On demand (Opportunity trigger)',
    triggerType: 'Manual / Webhook',
  },
  {
    id: 'design_bot',
    name: 'Raimond',
    color: '#4a8ff7',
    bg: 'rgba(74,143,247,0.1)',
    border: 'rgba(74,143,247,0.25)',
    role: 'Design & Visual',
    phase: 'phase-1',
    phaseLabel: 'Phase 1',
    description: 'Creates design packets including mockup direction, color palettes, CSS tokens, and Figma layout briefs. Translates brand guidelines into visual specifications.',
    capabilities: ['Mockup direction', 'CSS token generation', 'Figma brief writing', 'Brand-to-design mapping', 'QA checklist'],
    tools: ['CSD forms', 'Brand prompt library', 'Figma create intent', 'Visual comparison', 'Screenshot read'],
    outputs: ['Design work packet', 'Figma intent', 'Visual QA checklist'],
    handoffs: ['Devain for build packet and QA', 'Samaintha when copy gaps block design'],
    guardrails: ['New campaign files can be created freely', 'Existing Figma files require approval', 'Preserve client brand requirements'],
    queue: 'design-packets',
    approvalPolicy: 'Approval required for existing Figma edits',
    nextAction: 'Add Figma intent preview state and fixture visual diff result.',
    status: 'idle',
    lastRun: 'Yesterday 4:15 PM',
    runs: { total: 22, success: 20, failed: 2 },
    avgDuration: '11m 30s',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/campaign-design/SKILL.md',
    promptVersion: 'git commit at run time',
    schedule: 'On demand (Design request)',
    triggerType: 'Manual',
  },
  {
    id: 'dev_bot',
    name: 'Devain',
    color: '#f97316',
    bg: 'rgba(249,115,22,0.1)',
    border: 'rgba(249,115,22,0.25)',
    role: 'Engineering · Code Review · QA',
    phase: 'phase-1',
    phaseLabel: 'Phase 1',
    description: 'Generates implementation packets — app.js, pixel.js, company.json scaffolding — for CSD deals, and owns code review and QA: skills/campaign-dev carries the dev, review, and QA workflow references.',
    capabilities: ['Code generation', 'Code review', 'File scaffolding', 'QA automation', 'Diff production', 'Integration testing'],
    tools: ['App file read', 'Pixel file read', 'Git history', 'Browser QA', 'Pending Git action'],
    outputs: ['Development packet', 'Code review report', 'Code draft report', 'Browser QA requirements'],
    handoffs: ['Runs code review and QA internally', 'Braiks when repeated build rework appears'],
    guardrails: ['Generated code becomes a report or pending action', 'No autonomous Git or CSD writes', 'Browser QA results are audited'],
    queue: 'development-packets',
    approvalPolicy: 'Pending action for Git/CSD/task writes',
    nextAction: 'Resolve failed fixture around visual comparison timeout.',
    status: 'error',
    lastRun: '12:01 PM today (failed)',
    runs: { total: 19, success: 16, failed: 3 },
    avgDuration: '8m 12s',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/campaign-dev/SKILL.md',
    promptVersion: 'git commit at run time',
    schedule: 'On demand (Dev packet request)',
    triggerType: 'Manual',
  },
  {
    id: 'pm_bot',
    name: 'Baitty',
    color: '#14b8a6',
    bg: 'rgba(20,184,166,0.1)',
    border: 'rgba(20,184,166,0.25)',
    role: 'Project Management · Opportunity Shepherd',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Monitors active work queues and shepherds each opportunity through to completion, checking opportunity and task status at 7:20am, 1:20pm, and 5:20pm. Also covers Opportunity Approval validation and CSM follow-up drafting until dedicated skills exist.',
    capabilities: ['Queue monitoring', 'Opportunity shepherding', 'Approval validation', 'Task triage', 'SLA detection', 'CSM follow-up drafts'],
    tools: ['Salesforce Tasks', 'Opportunity summaries', 'AOP audit log', 'Pending actions'],
    outputs: ['Queue health report', 'Opportunity shepherd check', 'Triage packet', 'Assignee escalation draft'],
    handoffs: ['Devain for development and QA work', 'ChrisGPT for executive visibility'],
    guardrails: ['Draft-only escalations until Slack send is approved', 'Do not reassign tasks autonomously', 'Explain confidence from source data'],
    queue: 'project-monitor',
    approvalPolicy: 'Pending action for task comments or reassignment',
    nextAction: 'Registered on the */5 queue-monitor schedule — set AOP_ENABLED=1 to activate the worker.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/project-management/SKILL.md',
    promptVersion: 'planned',
    schedule: 'Every 5 minutes',
    triggerType: 'Scheduled',
  },
  // Bryain, Michail, Kairi, and Aishley are commented out: their duties are
  // folded into existing bots until dedicated skills justify a split.
  // Bryain (Opportunity Approval) + Aishley (CSM) -> pm_bot.
  // Michail (code review) + Kairi (QA) -> dev_bot (skills/campaign-dev ships
  // review-workflow.md and qa-workflow.md references).
  /*
  {
    id: 'bryain',
    name: 'Bryain',
    color: '#ef4444',
    bg: 'rgba(239,68,68,0.1)',
    border: 'rgba(239,68,68,0.25)',
    role: 'Opportunity Approval',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Validates Opportunity Approval readiness, required fields, missing CSD requirements, ownership, and launch risk before downstream agents start work.',
    capabilities: ['Approval validation', 'Missing-field detection', 'Risk scoring', 'Salesforce task review', 'Escalation drafting'],
    tools: ['Opportunity summary', 'Task summary', 'CSD lookup', 'Salesforce field glossary'],
    outputs: ['Approval readiness report', 'Missing requirement packet', 'Task comment draft'],
    handoffs: ['Samaintha when copy can start', 'Raimond when design can start', 'Baitty when human follow-up is needed'],
    guardrails: ['No Opportunity field updates without approval', 'Flag low-confidence field mappings', 'Never advance stage autonomously'],
    queue: 'opportunity-approval',
    approvalPolicy: 'Pending action for Opportunity or Task updates',
    nextAction: 'Map exact Salesforce fields from Workbench before enabling.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/salesforce-opportunity-task-triage/SKILL.md',
    promptVersion: 'planned',
    schedule: 'On approval queue change',
    triggerType: 'Webhook / Scheduled',
  },
  {
    id: 'michail',
    name: 'Michail',
    color: '#6366f1',
    bg: 'rgba(99,102,241,0.1)',
    border: 'rgba(99,102,241,0.25)',
    role: 'Code Review',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Reviews generated campaign code, implementation diffs, QA failures, and risky changes before they become approved Git or CSD updates.',
    capabilities: ['Code review', 'Diff analysis', 'Regression risk', 'Implementation critique', 'Review packet drafting'],
    tools: ['Git history', 'App file read', 'Pixel file read', 'QA logs', 'Screenshots'],
    outputs: ['Code review report', 'Required changes list', 'Approval recommendation'],
    handoffs: ['Devain for revisions', 'Kairi for QA confirmation', 'Braiks for recurring process failures'],
    guardrails: ['Review only, no autonomous commits', 'Keep findings source-linked', 'Separate blockers from recommendations'],
    queue: 'code-review',
    approvalPolicy: 'Pending action for Git comments or task updates',
    nextAction: 'Promote campaign-dev review workflow into a dedicated skill file.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/campaign-dev/references/review-workflow.md',
    promptVersion: 'planned',
    schedule: 'Every 10 minutes',
    triggerType: 'Scheduled',
  },
  {
    id: 'kairi',
    name: 'Kairi',
    color: '#06b6d4',
    bg: 'rgba(6,182,212,0.1)',
    border: 'rgba(6,182,212,0.25)',
    role: 'QA',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Executes campaign QA plans, captures browser evidence, compares screenshots, and turns pass/fail results into auditable QA reports.',
    capabilities: ['Browser QA', 'Screenshot capture', 'Console review', 'Network review', 'QA result reporting'],
    tools: ['Browser automation', 'Screenshot diff', 'Console logs', 'Network logs', 'QA checklist'],
    outputs: ['QA report', 'Failure evidence packet', 'Launch readiness summary'],
    handoffs: ['Devain for fixes', 'Michail for code review', 'Aishley for client-facing impact summaries'],
    guardrails: ['Evidence must include reproducible steps', 'No production-scale browser QA until Phase 2', 'Never mark launch-ready on incomplete tests'],
    queue: 'qa-runs',
    approvalPolicy: 'Pending action for task status updates',
    nextAction: 'Add browser QA fixtures with pass/fail examples.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/campaign-dev/references/qa-workflow.md',
    promptVersion: 'planned',
    schedule: 'Every 10 minutes',
    triggerType: 'Scheduled',
  },
  {
    id: 'aishley',
    name: 'Aishley',
    color: '#ec4899',
    bg: 'rgba(236,72,153,0.1)',
    border: 'rgba(236,72,153,0.25)',
    role: 'CSM Intelligence',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Prepares client-facing summaries, CSM follow-up drafts, support context, and account health notes when agent work has client impact.',
    capabilities: ['Client summary', 'Support context', 'Account health', 'CSM follow-up', 'Impact framing'],
    tools: ['Salesforce Account', 'Opportunity summary', 'CSD forms', 'Reports'],
    outputs: ['CSM follow-up draft', 'Client impact summary', 'Account context packet'],
    handoffs: ['ChrisGPT for executive visibility', 'Baitty for task routing', 'Ainna for performance evidence'],
    guardrails: ['Draft client language only', 'No email sends without approval', 'Separate confirmed facts from suggested framing'],
    queue: 'csm-intelligence',
    approvalPolicy: 'Pending action for Slack or email send',
    nextAction: 'Define CSM recipient mapping and approved tone examples.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/campaign-research/SKILL.md',
    promptVersion: 'planned',
    schedule: 'On client-impact event',
    triggerType: 'Event',
  },
  */
  {
    id: 'analytics_bot',
    name: 'Ainna',
    color: '#84cc16',
    bg: 'rgba(132,204,22,0.1)',
    border: 'rgba(132,204,22,0.25)',
    role: 'Data Analysis',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Analyzes revenue, performance, send-rate, affiliate, and campaign trends when reports or escalations need deeper quantitative evidence.',
    capabilities: ['Performance analysis', 'Trend detection', 'Forecast support', 'Revenue evidence', 'Data-source comparison'],
    tools: ['Reporting data', 'Salesforce read', 'Campaign metrics', 'CSV/XLSX analysis'],
    outputs: ['Analysis report', 'Metric explanation', 'Forecast note'],
    handoffs: ['ChrisGPT for executive report inclusion', 'Aishley for client summary', 'Braiks for process improvement'],
    guardrails: ['State data source and coverage', 'Do not infer unavailable metrics', 'Flag stale or incomplete data'],
    queue: 'data-analysis',
    approvalPolicy: 'Read-only by default',
    nextAction: 'Registered on the weekly Monday schedule; live reporting reads replace fixtures when read adapters go live.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/data-analysis/SKILL.md',
    promptVersion: 'planned',
    schedule: 'On report request',
    triggerType: 'Manual / Event',
  },
  {
    id: 'process_bot',
    name: 'Braiks',
    color: '#a855f7',
    bg: 'rgba(168,85,247,0.1)',
    border: 'rgba(168,85,247,0.25)',
    role: 'Process Improvement',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Reviews repeated delays, rework loops, unclear requirements, and handoff breakdowns, then recommends process changes and instruction updates.',
    capabilities: ['Process review', 'Failure pattern analysis', 'Retrospective summary', 'Instruction improvement', 'Metric recommendations'],
    tools: ['Audit log', 'Workflow runs', 'Task history', 'Reports', 'Skill docs'],
    outputs: ['Process review', 'Improvement backlog', 'Instruction change proposal'],
    handoffs: ['ChrisGPT for leadership summary', 'Baitty for queue policy changes', 'Agent owners for prompt updates'],
    guardrails: ['Recommend changes only', 'Keep blame out of summaries', 'Tie patterns to evidence'],
    queue: 'process-review',
    approvalPolicy: 'Pending action for instruction or workflow changes',
    nextAction: 'Registered on the weekly Friday schedule; reviews AOP audit history and run outcomes.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/process-review/SKILL.md',
    promptVersion: 'planned',
    schedule: 'Weekly or on recurring failure',
    triggerType: 'Scheduled / Event',
  },
  // ── COP — Campaign Optimization agents (monitored by AOP) ──
  {
    id: 'segment_bot',
    name: 'Segment Intelligence',
    platform: 'cop',
    color: '#22d3ee',
    bg: 'rgba(34,211,238,0.1)',
    border: 'rgba(34,211,238,0.25)',
    role: 'Behavioral Segmentation',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Re-clusters account behavioral data into prioritized segments with explainability and activation readiness, and proposes the recommended solution family per segment.',
    capabilities: ['Behavioral clustering', 'Segment scoring', 'Confidence estimation', 'Explainability narrative', 'Re-cluster on drift'],
    tools: ['COP read models', 'Event/session signals', 'Audit log', 'Reports'],
    outputs: ['Segment set', 'Segment intelligence report', 'Recommended solution families'],
    handoffs: ['Opportunity Scout for ranking', 'Campaign Draft for activation'],
    guardrails: ['Read-only source queries', 'Raw events never persisted in COP', 'Cite evidence in every segment'],
    queue: 'cop-segments',
    approvalPolicy: 'Read-only — produces reports, never direct writes',
    nextAction: 'Add COP skill + fixture so the recluster workflow runs through the shared AOP runner.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/cop-segment/SKILL.md',
    promptVersion: 'planned',
    schedule: 'On demand / drift trigger',
    triggerType: 'Manual / Event',
  },
  {
    id: 'opportunity_bot',
    name: 'Opportunity Scout',
    platform: 'cop',
    color: '#4a8ff7',
    bg: 'rgba(74,143,247,0.1)',
    border: 'rgba(74,143,247,0.25)',
    role: 'Opportunity Detection',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Scans segments, funnels, and campaign performance to detect and rank the highest-value revenue opportunities with projected impact.',
    capabilities: ['Opportunity detection', 'Revenue ranking', 'Funnel-stage diagnosis', 'Projected impact estimates'],
    tools: ['COP read models', 'Reports', 'Audit log'],
    outputs: ['Ranked opportunities', 'Opportunity scan report'],
    handoffs: ['Campaign Draft for activation', 'Intelligence Reporting for QBR rollups'],
    guardrails: ['Read-only source queries', 'Projections labeled as projections', 'Cite source records'],
    queue: 'cop-opportunities',
    approvalPolicy: 'Read-only — produces reports, never direct writes',
    nextAction: 'Add COP skill + fixture so the opportunity scan runs through the shared AOP runner.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/cop-opportunity/SKILL.md',
    promptVersion: 'planned',
    schedule: 'On demand / scheduled scan',
    triggerType: 'Manual / Scheduled',
  },
  {
    id: 'campaign_draft_bot',
    name: 'Campaign Draft',
    platform: 'cop',
    color: '#f0198c',
    bg: 'rgba(240,25,140,0.1)',
    border: 'rgba(240,25,140,0.25)',
    role: 'Campaign Activation',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Drafts a launch-ready campaign for a target segment — creative, offer, targeting rules — and submits it to the approval queue. Never launches directly.',
    capabilities: ['Campaign drafting', 'Offer/targeting assembly', 'Readiness checks', 'Launch packet generation'],
    tools: ['COP read models', 'CSD forms', 'Campaign templates', 'Pending action (launch)'],
    outputs: ['Campaign draft', 'Launch readiness packet', 'Pending launch action'],
    handoffs: ['Samaintha for copy', 'Raimond for design', 'Experiment Monitor after launch'],
    guardrails: ['Launch is a governed action — pending approval', 'Honor global suppressions', 'Margin impact shown honestly'],
    queue: 'cop-campaigns',
    approvalPolicy: 'Pending action for campaign launch (internal → client)',
    nextAction: 'Add COP skill + fixture so the campaign draft raises a pending launch action through the shared AOP service.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-opus-4-8',
    skillSource: 'skills/cop-campaign-draft/SKILL.md',
    promptVersion: 'planned',
    schedule: 'On demand (segment trigger)',
    triggerType: 'Manual / Webhook',
  },
  {
    id: 'experiment_bot',
    name: 'Experiment Monitor',
    platform: 'cop',
    color: '#a855f7',
    bg: 'rgba(168,85,247,0.1)',
    border: 'rgba(168,85,247,0.25)',
    role: 'Experimentation',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Monitors live experiments, tracks allocation and confidence, and submits a winner declaration to the approval queue when the decision rule is met.',
    capabilities: ['Experiment monitoring', 'Allocation tracking', 'Confidence/significance checks', 'Winner detection'],
    tools: ['COP read models', 'Reports', 'Pending action (winner declaration)'],
    outputs: ['Experiment status report', 'Pending winner-declaration action'],
    handoffs: ['Intelligence Reporting for lift results', 'Campaign Draft for rollout'],
    guardrails: ['Winner declaration is governed — pending approval', 'Respect configured confidence + length', 'Guardrail metrics must stay clean'],
    queue: 'cop-experiments',
    approvalPolicy: 'Pending action for winner declaration',
    nextAction: 'Add COP skill + fixture so the monitor raises a pending winner-declaration through the shared AOP service.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-sonnet-4-6',
    skillSource: 'skills/cop-experiment/SKILL.md',
    promptVersion: 'planned',
    schedule: 'Recurring while experiments are live',
    triggerType: 'Scheduled / Event',
  },
  {
    id: 'reporting_bot',
    name: 'Intelligence Reporting',
    platform: 'cop',
    color: '#10b981',
    bg: 'rgba(16,185,129,0.1)',
    border: 'rgba(16,185,129,0.25)',
    role: 'Executive Reporting',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Produces client-facing intelligence reports — influenced revenue, conversion lift, campaign effectiveness, and next-best opportunities — for QBRs and scheduled delivery.',
    capabilities: ['Executive report generation', 'Lift/incrementality rollups', 'Funnel aggregation', 'Scheduled delivery'],
    tools: ['COP read models', 'Reports', 'Audit log'],
    outputs: ['Intelligence report', 'Scheduled report deliveries'],
    handoffs: ['ChrisGPT for portfolio rollup', 'Opportunity Scout for next-best actions'],
    guardrails: ['Holdout-measured where marked', 'Projections labeled as projections', 'Defensible to a VP'],
    queue: 'cop-reports',
    approvalPolicy: 'Read-only — produces reports, never direct writes',
    nextAction: 'Add COP skill + fixture so the intelligence report runs through the shared AOP runner.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-opus-4-8',
    skillSource: 'skills/cop-reporting/SKILL.md',
    promptVersion: 'planned',
    schedule: 'Scheduled (daily/weekly/monthly)',
    triggerType: 'Scheduled / Manual',
  },
  {
    id: 'research_bot',
    name: 'Deep Research',
    platform: 'cop',
    color: '#f7931a',
    bg: 'rgba(247,147,26,0.1)',
    border: 'rgba(247,147,26,0.25)',
    role: 'Research & Evidence',
    phase: 'phase-2',
    phaseLabel: 'Phase 2',
    description: 'Answers open-ended operator questions with cited evidence and source freshness, synthesizing behavioral, campaign, and catalog data into a defensible answer.',
    capabilities: ['Question answering', 'Evidence synthesis', 'Source freshness checks', 'Citation tracking'],
    tools: ['COP read models', 'Reports', 'Audit log'],
    outputs: ['Research answer with citations', 'Deep research report'],
    handoffs: ['Opportunity Scout for follow-up', 'Intelligence Reporting for inclusion'],
    guardrails: ['Cite every claim', 'Surface source freshness', 'Never invent metrics'],
    queue: 'cop-research',
    approvalPolicy: 'Read-only — produces reports, never direct writes',
    nextAction: 'Add COP skill + fixture so deep research runs through the shared AOP runner.',
    status: 'planned',
    lastRun: 'Not scheduled',
    runs: { total: 0, success: 0, failed: 0 },
    avgDuration: 'n/a',
    model: 'claude-opus-4-8',
    skillSource: 'skills/cop-research/SKILL.md',
    promptVersion: 'planned',
    schedule: 'On demand (operator query)',
    triggerType: 'Manual',
  },
];

const STATUS_DOT = { active: '#34c759', idle: '#9ca3c5', error: '#ff625d', planned: '#4a8ff7' };
const STATUS_LABEL = { active: 'Active', idle: 'Idle', error: 'Error', planned: 'Planned' };

function AgentCard({ agent, onSelect, selected }) {
  const rate = agent.runs.total > 0 ? `${Math.round((agent.runs.success / agent.runs.total) * 100)}%` : 'n/a';
  return (
    <div
      className={`aop-agent-card${selected ? ' selected' : ''}`}
      style={{ '--agent-color': agent.color, '--agent-bg': agent.bg, '--agent-border': agent.border }}
      onClick={() => onSelect(agent)}
    >
      <div className="aop-agent-card-header">
        <div className="aop-agent-card-avatar" style={{ background: agent.bg, border: `2px solid ${agent.border}`, color: agent.color }}>
          {agent.name[0]}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="aop-agent-card-name">{agent.name}</div>
          <div className="aop-agent-card-role">{agent.role}</div>
        </div>
        <div className="aop-agent-status-pill" style={{ background: `${STATUS_DOT[agent.status]}22`, color: STATUS_DOT[agent.status] }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_DOT[agent.status], display: 'inline-block', marginRight: 5 }} />
          {STATUS_LABEL[agent.status]}
        </div>
      </div>

      <p className="aop-agent-card-desc">{agent.description}</p>

      <div className="aop-agent-stats">
        <div className="aop-agent-stat">
          <div className="aop-agent-stat-val">{agent.runs.total}</div>
          <div className="aop-agent-stat-key">Runs</div>
        </div>
        <div className="aop-agent-stat">
          <div className="aop-agent-stat-val" style={{ color: agent.runs.total > 0 ? '#34c759' : 'var(--text-sec)' }}>{rate}</div>
          <div className="aop-agent-stat-key">Success</div>
        </div>
        <div className="aop-agent-stat">
          <div className="aop-agent-stat-val">{agent.runs.failed}</div>
          <div className="aop-agent-stat-key">Failed</div>
        </div>
        <div className="aop-agent-stat">
          <div className="aop-agent-stat-val">{agent.avgDuration}</div>
          <div className="aop-agent-stat-key">Avg Run</div>
        </div>
      </div>

      <div className="aop-agent-card-footer">
        <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>Last: {agent.lastRun}</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span className={`aop-badge ${agent.phase}`}>{agent.phaseLabel}</span>
          <span className="aop-badge" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-sec)' }}>{agent.triggerType}</span>
        </div>
      </div>
    </div>
  );
}

function TagList({ items, color, bg, border }) {
  return (
    <div className="aop-agent-tag-list">
      {items.map(item => (
        <span key={item} style={{ background: bg, border: `1px solid ${border}`, color }}>
          {item}
        </span>
      ))}
    </div>
  );
}

function DetailList({ items }) {
  return (
    <ul className="aop-agent-detail-list">
      {items.map(item => <li key={item}>{item}</li>)}
    </ul>
  );
}

function AgentDetail({ agent, onViewChange }) {
  return (
    <div className="aop-agent-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: agent.bg, border: `2px solid ${agent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, color: agent.color }}>
          {agent.name[0]}
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{agent.name}</div>
          <div style={{ fontSize: 13, color: 'var(--text-sec)' }}>{agent.role}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span className={`aop-badge ${agent.phase}`}>{agent.phaseLabel}</span>
          <div className="aop-agent-status-pill" style={{ background: `${STATUS_DOT[agent.status]}22`, color: STATUS_DOT[agent.status] }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_DOT[agent.status], display: 'inline-block', marginRight: 6 }} />
            {STATUS_LABEL[agent.status]}
          </div>
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-sec)', lineHeight: 1.65, marginBottom: 24 }}>{agent.description}</p>

      <div className="aop-card" style={{ marginBottom: 16 }}>
        <div className="aop-card-header"><span className="aop-card-title">Configuration</span></div>
        <div className="aop-card-body">
          {[
            ['Model',    agent.model],
            ['Skill',    agent.skillSource],
            ['Version',  agent.promptVersion],
            ['Queue',    agent.queue],
            ['Schedule', agent.schedule],
            ['Trigger',  agent.triggerType],
            ['Approval', agent.approvalPolicy],
            ...(agent.registered ? [['Tokens (all time)', String(agent.tokensTotal)]] : []),
          ].map(([k, v]) => (
            <div key={k} className="aop-health-row">
              <span className="aop-health-label">{k}</span>
              <span className="aop-health-value" style={{ color: 'var(--text)', fontFamily: 'monospace', fontSize: 11 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      <button className="aop-btn primary" style={{ marginBottom: 16 }} onClick={() => onViewChange?.('skill-docs')}>
        View Skill Instructions
      </button>

      <div className="aop-card">
        <div className="aop-card-header"><span className="aop-card-title">Capabilities</span></div>
        <div className="aop-card-body">
          <TagList items={agent.capabilities} color={agent.color} bg={agent.bg} border={agent.border} />
        </div>
      </div>

      <div className="aop-card" style={{ marginTop: 16 }}>
        <div className="aop-card-header"><span className="aop-card-title">Tool Access</span></div>
        <div className="aop-card-body">
          <TagList items={agent.tools} color="var(--text-sec)" bg="rgba(255,255,255,0.04)" border="var(--border)" />
        </div>
      </div>

      <div className="aop-grid-2" style={{ marginTop: 16 }}>
        <div className="aop-card">
          <div className="aop-card-header"><span className="aop-card-title">Outputs</span></div>
          <div className="aop-card-body"><DetailList items={agent.outputs} /></div>
        </div>
        <div className="aop-card">
          <div className="aop-card-header"><span className="aop-card-title">Handoffs</span></div>
          <div className="aop-card-body"><DetailList items={agent.handoffs} /></div>
        </div>
      </div>

      <div className="aop-card" style={{ marginTop: 16 }}>
        <div className="aop-card-header"><span className="aop-card-title">Guardrails</span></div>
        <div className="aop-card-body">
          <DetailList items={agent.guardrails} />
          <div className="aop-agent-next-action">
            <span>Next setup action</span>
            <p>{agent.nextAction}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDuration(ms) {
  if (ms == null) return 'n/a';
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

// Merge a live registry/metrics record onto a catalog entry.
function mergeLive(catalogAgent, live) {
  if (!live) return catalogAgent;
  return {
    ...catalogAgent,
    registered: true,
    status: live.runs.running > 0 ? 'active' : 'idle',
    lastRun: live.agentRuns.lastRunAt ? formatWhen(live.agentRuns.lastRunAt) : 'Never',
    runs: { total: live.runs.total, success: live.runs.completed, failed: live.runs.failed },
    avgDuration: formatDuration(live.runs.avgDurationMs),
    model: live.agentRuns.lastModel || '— (no runs yet)',
    promptVersion: live.skillVersion === 'unknown' ? 'unknown' : live.skillVersion.slice(0, 12),
    schedule: live.schedules.length ? live.schedules.map(s => s.cron).join(' · ') : 'On demand',
    triggerType: live.schedules.length ? 'Scheduled' : 'Manual / API',
    tokensTotal: live.agentRuns.tokensTotal,
    skillSource: `${live.skillDir}/SKILL.md`,
  };
}

export default function Agents({ onViewChange }) {
  const [liveAgents, setLiveAgents] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(AGENTS[0].id);
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');

  useEffect(() => {
    aopApi
      .agents()
      .then((res) => {
        if (!res.success) throw new Error(res.error || 'Failed to load agents');
        setLiveAgents(res.agents || []);
        setError(null);
      })
      .catch((err) => setError(err.message));
  }, []);

  const agents = AGENTS.map(agent => mergeLive(agent, liveAgents && liveAgents.find(l => l.name === agent.id)));
  const selected = agents.find(a => a.id === selectedId) || agents[0];

  const filteredAgents = agents.filter(agent => (
    (phaseFilter === 'all' || agent.phase === phaseFilter) &&
    (statusFilter === 'all' || agent.status === statusFilter) &&
    (platformFilter === 'all' || (agent.platform || 'aop') === platformFilter)
  ));

  const totals = [
    { label: 'Total Agents', value: agents.length, color: 'var(--text)' },
    { label: 'Registered', value: agents.filter(a => a.registered).length, color: 'var(--green)' },
    { label: 'Planned', value: agents.filter(a => !a.registered).length, color: 'var(--blue)' },
    { label: 'Runs Recorded', value: agents.reduce((sum, a) => sum + (a.registered ? a.runs.total : 0), 0), color: 'var(--purple)' },
  ];

  return (
    <div>
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Agents</h1>
          <p className="aop-page-subtitle">Full agent roster across AOP operations and COP campaign optimization — live run metrics, skills, tools, outputs, and handoffs</p>
        </div>
        <div className="aop-page-actions">
          {liveAgents && <span className="aop-badge completed">Live metrics</span>}
          <select className="aop-pill" value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} style={{ width: 'auto', paddingRight: 28 }}>
            <option value="all">All platforms</option>
            <option value="aop">AOP</option>
            <option value="cop">COP</option>
          </select>
          <select className="aop-pill" value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)} style={{ width: 'auto', paddingRight: 28 }}>
            <option value="all">All phases</option>
            <option value="phase-1">Phase 1</option>
            <option value="phase-2">Phase 2</option>
          </select>
          <select className="aop-pill" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 'auto', paddingRight: 28 }}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="idle">Idle</option>
            <option value="planned">Planned</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="aop-stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        {totals.map(stat => (
          <div key={stat.label} className="aop-stat-card aop-agent-summary-card">
            <div className="aop-stat-value" style={{ color: stat.color }}>{stat.value}</div>
            <div className="aop-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="aop-agents-layout">
        <div className="aop-agents-grid">
          {filteredAgents.map(a => (
            <AgentCard key={a.id} agent={a} onSelect={(agent) => setSelectedId(agent.id)} selected={selected?.id === a.id} />
          ))}
          {filteredAgents.length === 0 && (
            <div className="aop-empty">
              <div className="aop-empty-icon">No matches</div>
              <div>Adjust the phase or status filters.</div>
            </div>
          )}
        </div>
        {selected && (
          <div className="aop-agents-panel">
            <AgentDetail agent={selected} onViewChange={onViewChange} />
          </div>
        )}
      </div>
    </div>
  );
}
